import * as React from 'react'
import * as z from 'zod'

import { Position } from '@basehub/data/replicache/mutators/repo-space/block/helpers/fractional-indexing'
import { clsx } from 'clsx'
import { DragEndEvent } from '@dnd-kit/core'
import { DndController } from '../../../../dnd-provider'
import { getId } from '@basehub/utils/id/index'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { toast } from 'sonner'
import { isValidDnd } from '../table/dnd/droppable-zones'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import type {
  Block,
  CommonBlocksCompoundIndexes,
  ListBlock,
} from '@basehub/data/replicache/index'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RepoUndoManager,
  RepoUndoOps,
} from '~/replicache/frontend/undo-redo/manager'
import { ReorderBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/reorder'
import { create } from 'zustand'

export const DroppableProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <>
      <WindowDragDetector />
      {children}
    </>
  )
}

const WindowDragDetector: React.FC = React.memo(() => {
  const setWindowDragActive = useDragStore((state) => state.setWindowDragActive)

  React.useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault()
      setWindowDragActive(true)
    }

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault()
      if (e.relatedTarget && document.contains(e.relatedTarget as Node)) {
        // If it's a child, do nothing (ignore this dragleave event)
        return
      }
      setWindowDragActive(false)
    }

    const handleDrop = () => {
      setWindowDragActive(false)
    }

    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('drop', handleDrop)
    }
  }, [setWindowDragActive])

  return null
})

interface DragState {
  activeZoneId: string | null
  dragCounters: Record<string, number>
  isWindowDragActive: boolean
  setActiveZoneId: (id: string | null) => void
  incrementDragCounter: (id: string) => void
  decrementDragCounter: (id: string) => void
  resetDragCounter: (id: string) => void
  setWindowDragActive: (active: boolean) => void
}

export const useDragStore = create<DragState>((set) => ({
  activeZoneId: null,
  dragCounters: {},
  isWindowDragActive: false,
  setActiveZoneId: (id) => set({ activeZoneId: id }),
  incrementDragCounter: (id) =>
    set((state) => ({
      dragCounters: {
        ...state.dragCounters,
        [id]: (state.dragCounters[id] || 0) + 1,
      },
    })),
  decrementDragCounter: (id) =>
    set((state) => ({
      dragCounters: {
        ...state.dragCounters,
        [id]: Math.max(0, (state.dragCounters[id] || 0) - 1),
      },
    })),
  resetDragCounter: (id) =>
    set((state) => ({
      dragCounters: {
        ...state.dragCounters,
        [id]: 0,
      },
    })),
  setWindowDragActive: (active) => set({ isWindowDragActive: active }),
}))

export type DroppableZoneProps = {
  parentBlock: ListBlock
  parentPath: string
  index: number
  id: Block['id'] | null
  style?: React.JSX.IntrinsicElements['span']['style']
  onDropFilesCallback?: (createdRows: Block[]) => void
}

export const DroppableZone = React.memo(
  ({
    style,
    id,
    zoneId,
    onDrop,
    className = '',
    indicatorClassName = '',
    onDropFilesCallback,
  }: {
    zoneId: string
    onDrop: (
      e: React.DragEvent<HTMLElement>,
      position: Position
    ) => Promise<Block[]>
    indicatorClassName?: string
    className?: string
  } & Pick<DroppableZoneProps, 'onDropFilesCallback' | 'style' | 'id'>) => {
    const dragCounter = useDragStore(selectDragCounter(zoneId))
    const incrementDragCounter = useDragStore(
      (state) => state.incrementDragCounter
    )
    const decrementDragCounter = useDragStore(
      (state) => state.decrementDragCounter
    )
    const setWindowDragActive = useDragStore(
      (state) => state.setWindowDragActive
    )
    const resetDragCounter = useDragStore((state) => state.resetDragCounter)
    const isWindowDragActive = useDragStore(selectIsWindowDragActive)
    const handleDragEnter = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        incrementDragCounter(zoneId)
      },
      [zoneId, incrementDragCounter]
    )

    const handleDragLeave = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        decrementDragCounter(zoneId)
      },
      [zoneId, decrementDragCounter]
    )

    const handleDrop = React.useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault()
        resetDragCounter(zoneId)
        setWindowDragActive(false)
        // set windowdragactive to false
        const createdRows = await onDrop(
          e as React.DragEvent<HTMLElement>,
          id ? { type: 'before', targetId: id } : { type: 'first' }
        )
        onDropFilesCallback?.(createdRows)
      },
      [
        id,
        onDrop,
        onDropFilesCallback,
        resetDragCounter,
        setWindowDragActive,
        zoneId,
      ]
    )

    return (
      <>
        <span
          style={style}
          className={clsx(
            className,
            !isWindowDragActive ? 'pointer-events-none' : 'pointer-events-auto'
          )}
          onDragOver={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={handleDrop}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
        />
        <DropIndicator
          style={style}
          isVisible={dragCounter > 0}
          className={indicatorClassName}
        />
      </>
    )
  }
)

const selectDragCounter = (id: string) => (state: DragState) =>
  state.dragCounters[id] || 0

export const DropIndicator = React.memo(
  ({
    style,
    isVisible,
    className,
  }: {
    style?: React.CSSProperties
    isVisible: boolean
    className?: string
  }) => {
    if (!isVisible) return null

    return <span style={style} className={className} />
  }
)

const selectIsWindowDragActive = (state: DragState) => state.isWindowDragActive

// setup

export const dndType = 'list-dnd'

export const listBlockDndControllers = {
  dndType,
  onDragEnd: (event: DragEndEvent) => {
    const blockId = event.active.data?.current?.shouldDropTo

    if (!event.over || !blockId) return

    const parsedDraggable =
      listBlockDndControllers.draggableDataSchema.safeParse(
        event.active.data.current
      )
    const parsedDroppable =
      listBlockDndControllers.droppableDataSchema.safeParse(
        event.over?.data.current
      )

    if (!parsedDraggable.success || !parsedDroppable.success) return

    const draggable = parsedDraggable.data
    const dropable = parsedDroppable.data
    const treeNode = document.querySelector(`#${dndType}-${blockId}`)

    if (treeNode) {
      const customEvent = new CustomEvent('dnd', {
        detail: {
          draggedItems: draggable,
          dropTarget: dropable,
        },
      })
      treeNode.dispatchEvent(customEvent)
    } else throw new Error('treeNode not found')
  },
  droppableDataSchema: z.object({
    intendedIndex: z.number(),
    type: z.literal(dndType),
  }),
  draggableDataSchema: z.object({
    keysDragged: z.set(z.object({ label: z.string(), id: z.string() })),
    type: z.literal(dndType),
    index: z.number(),
    shouldDropTo: z.string(),
  }),
} satisfies DndController

export function useListDndTreeNode({
  rootId,
  block,
  enabled,
  treeManager,
  onDropCallback,
  undo,
}: {
  rootId: string
  block: ListBlock
  enabled: boolean
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  onDropCallback?: (
    newIndex: number,
    keysDragged: Set<{ label: string; id: string }>
  ) => void
  undo: {
    manager: RepoUndoManager | null
    focusOp: RepoUndoOps[number]
  }
}) {
  const replicache = useRepoReplicache().data?.replicache
  const targetId = block.id ?? ''
  const sourceBlockId = idToSourceBlockId(block)

  // receive dnd events here
  React.useEffect(() => {
    if (!enabled) return
    const contentEl = document.querySelector(
      `#${listBlockDndControllers.dndType}-${targetId}`
    )
    if (!contentEl) return

    const handleDnd = async (event: unknown) => {
      if (!replicache) throw new Error('replicache not found')
      if (!treeManager) throw new Error('treeManager not found')

      const dndResult = z
        .object({
          detail: z.object({
            draggedItems: listBlockDndControllers.draggableDataSchema,
            dropTarget: listBlockDndControllers.droppableDataSchema,
          }),
        })
        .parse(event).detail

      if (
        isValidDnd({
          intendedIndex: dndResult.dropTarget.intendedIndex,
          dragItemIndex: dndResult.draggedItems.index,
          isDraggingTo: targetId,
          parentBlockId: dndResult.draggedItems.shouldDropTo,
        })
      ) {
        const keysDraggedArray = Array.from(dndResult.draggedItems.keysDragged)

        const getKey = (node: TreeNode<Block>) => idToSourceBlockId(node.value)

        // 1. sort by sortKey
        const flatSortedTree = treeManager.getFlatSortedTree__EXPENSIVE()
        const sortedKeysDragged = keysDraggedArray.sort((a, b) => {
          const nodeAIndex = flatSortedTree.findIndex(
            (n) =>
              getKey(treeManager.getNode(n.key, { required: true })) === a.id
          )
          const nodeBIndex = flatSortedTree.findIndex(
            (n) =>
              getKey(treeManager.getNode(n.key, { required: true })) === b.id
          )
          return nodeAIndex - nodeBIndex
        })

        // 2. update the intendedIndex on an edge case:
        // if one of the dragged items is at the same level of the drop target
        // AND the dragged item has a lesser index than the drop target
        // the new intended index should be intendedIndex - quantityOfIndexesToAdjust
        const dropNode = treeManager.getNode(targetId)
        if (!dropNode) {
          throw new Error(`dropNode ${targetId} not found`)
        }

        // 3. apply sort and filter mutations
        dndResult.draggedItems.keysDragged = new Set(sortedKeysDragged)

        const params: ReorderBlockParams = {
          rootId,
          blockIds: Array.from(dndResult.draggedItems.keysDragged).map(
            ({ id }) => id
          ),
          targetBlockId: targetId,
          intendedIndex: dndResult.dropTarget.intendedIndex,
          isoNow: new Date().toISOString(),
          idSeed: getId(),
        }
        await replicache.mutate
          .reorderBlocks(params)
          .then((result) => {
            undo.manager?.push({
              redo: [
                undo.focusOp,
                { op: 'reorderBlocks', args: params },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
              undo: [
                undo.focusOp,
                { op: 'blockBatchMutations', args: { mutations: result.undo } },
                {
                  op: 'customWindowEvent',
                  args: {
                    name: `${sourceBlockId}-ur-custom-event`,
                    detail: { refresh: true },
                  },
                },
              ],
            })
            return onDropCallback?.(
              dndResult.dropTarget.intendedIndex,
              dndResult.draggedItems.keysDragged
            )
          })
          .catch((err) => {
            console.error(err)
            if (err instanceof MutatorUserError) {
              toast.error(err.reason)
            } else {
              toast.error('An error occurred')
            }
          })
      }
    }

    contentEl.addEventListener('dnd', handleDnd)

    return () => {
      contentEl.removeEventListener('dnd', handleDnd)
    }
  }, [
    replicache,
    rootId,
    targetId,
    enabled,
    treeManager,
    onDropCallback,
    undo.focusOp,
    undo.manager,
    sourceBlockId,
  ])
}
